import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

/**
 * Reset-password page (Phase 1A).
 *
 * Reads `?token=<...>` from the URL, posts to /auth/reset-password with the
 * new password. On success redirects to /login with a flash message.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError("Missing or invalid token / Token galat hai");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    if (pwd !== pwd2) {
      setError("Passwords do not match / Password match nahi kar raha");
      setSubmitting(false);
      return;
    }
    if (pwd.length < 8) {
      setError("Password must be at least 8 characters / Kam se kam 8 chars chahiye");
      setSubmitting(false);
      return;
    }
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        Array.isArray(detail) ? detail[0]?.msg || "Could not reset password" :
        detail || "Could not reset password / Reset nahi ho paya"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="h-12 w-12 rounded-md bg-white border border-slate-200 flex items-center justify-center p-1">
              <img src="/bravura_logo.png" alt="Bravura" className="h-full w-full object-contain" />
            </div>
            <div className="text-left">
              <div className="text-xs uppercase tracking-widest text-slate-500">Bravura</div>
              <div className="font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
                HR Portal
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-7">
          <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Chivo" }}>
            Reset password
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            Choose a new password — 8+ characters with uppercase, number & special char.
            <br />
            <span className="text-slate-400">Naya password chunein — 8+ chars, uppercase, number & special char.</span>
          </p>

          {success ? (
            <div
              data-testid="reset-success"
              className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-3"
            >
              <div className="font-semibold mb-1">Password updated ✓</div>
              Redirecting to login... / Login pe le ja rahe hain...
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="pwd" className="text-xs uppercase tracking-wider text-slate-500">
                  New password
                </Label>
                <Input
                  id="pwd"
                  data-testid="reset-new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="pwd2" className="text-xs uppercase tracking-wider text-slate-500">
                  Confirm new password
                </Label>
                <Input
                  id="pwd2"
                  data-testid="reset-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  className="mt-1.5"
                />
              </div>

              {error && (
                <div
                  data-testid="reset-error"
                  className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                data-testid="reset-submit-btn"
                disabled={submitting || !token}
                className="w-full brand-bg brand-bg-hover text-white font-medium"
              >
                {submitting ? "Updating..." : "Update password"}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              data-testid="reset-back-to-login"
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              ← Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
